import React, { useState, useEffect, useCallback } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { performanceApi } from '../../services/api';
import { formatBytes } from '../../utils/stringUtils';
import { Navigate } from 'react-router-dom';

// Register Chart.js components
Chart.register(...registerables);

const PerformanceMonitoring = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [systemMetrics, setSystemMetrics] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [endpointMetrics, setEndpointMetrics] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [performanceEnabled, setPerformanceEnabled] = useState(true); // Assume enabled until we know otherwise

  // Check if performance monitoring is enabled
  useEffect(() => {
    const checkPerformanceEnabled = async () => {
      try {
        const response = await performanceApi.isEnabled();
        setPerformanceEnabled(response.data.enabled);
      } catch (error) {
        console.error('Error checking if performance monitoring is enabled:', error);
        setPerformanceEnabled(false);
      }
    };
    
    checkPerformanceEnabled();
  }, []);

  // Function to fetch all metrics
  const fetchMetrics = useCallback(async () => {
    if (!performanceEnabled) return;
    try {
      setLoading(true);
      
      // Get API metrics
      const metricsResponse = await performanceApi.getMetrics();
      setMetrics(metricsResponse.data);
      
      // Get system metrics
      const systemResponse = await performanceApi.getSystemMetrics();
      setSystemMetrics(systemResponse.data);
      
      // If an endpoint is selected, get detailed metrics for it
      if (selectedEndpoint) {
        const endpointResponse = await performanceApi.getEndpointMetrics(selectedEndpoint);
        setEndpointMetrics(endpointResponse.data);
      }
      
      setLastUpdated(new Date());
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error fetching performance metrics:', err);
      setError('Failed to load performance metrics');
      setLoading(false);
    }
  }, [selectedEndpoint]);

  // Initial data load
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Set up auto-refresh
  useEffect(() => {
    let intervalId;
    
    if (autoRefresh) {
      intervalId = setInterval(() => {
        fetchMetrics();
      }, refreshInterval * 1000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, refreshInterval, fetchMetrics]);

  // Handle endpoint selection
  const handleEndpointSelect = async (endpoint) => {
    try {
      setLoading(true);
      setSelectedEndpoint(endpoint);
      setEndpointMetrics(null); // Clear previous metrics while loading
      
      const response = await performanceApi.getEndpointMetrics(endpoint);
      console.log(`Endpoint metrics for ${endpoint}:`, response.data);
      
      // Check if the response data has the expected structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response data format');
      }
      
      // Ensure the response has all required fields
      const requiredFields = ['count', 'avg_response_time', 'min_response_time', 'max_response_time', 
                             'p50_response_time', 'p75_response_time', 'p90_response_time', 'p95_response_time', 
                             'p99_response_time', 'recent_times'];
      
      const missingFields = requiredFields.filter(field => response.data[field] === undefined);
      if (missingFields.length > 0) {
        console.warn(`Response missing required fields: ${missingFields.join(', ')}`);
        
        // Create default values for missing fields to prevent rendering errors
        const defaultData = { ...response.data };
        missingFields.forEach(field => {
          if (field === 'count') defaultData[field] = 0;
          else if (field === 'recent_times') defaultData[field] = [];
          else defaultData[field] = 0;
        });
        
        setEndpointMetrics(defaultData);
      } else {
        setEndpointMetrics(response.data);
      }
      
      setLoading(false);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error(`Error fetching metrics for endpoint ${endpoint}:`, err);
      setError(`Failed to load metrics for endpoint ${endpoint}: ${err.message}`);
      setLoading(false);
    }
  };

  // Format milliseconds to a readable format
  const formatTime = (ms) => {
    if (ms < 1) {
      return `${(ms * 1000).toFixed(2)} μs`;
    } else if (ms < 1000) {
      return `${ms.toFixed(2)} ms`;
    } else {
      return `${(ms / 1000).toFixed(2)} s`;
    }
  };

  // Prepare chart data for endpoint response times
  const prepareEndpointChartData = () => {
    if (!metrics || !metrics.endpoints) return null;
    
    const endpoints = Object.keys(metrics.endpoints).slice(0, 10); // Limit to top 10 endpoints
    const avgResponseTimes = endpoints.map(endpoint => metrics.endpoints[endpoint].avg_response_time * 1000); // Convert to ms
    const requestCounts = endpoints.map(endpoint => metrics.endpoints[endpoint].count);
    
    return {
      labels: endpoints,
      datasets: [
        {
          label: 'Avg Response Time (ms)',
          data: avgResponseTimes,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          label: 'Request Count',
          data: requestCounts,
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          borderColor: 'rgba(153, 102, 255, 1)',
          borderWidth: 1,
          yAxisID: 'y1',
        }
      ]
    };
  };

  // Prepare chart data for detailed endpoint metrics
  const prepareDetailedEndpointChartData = () => {
    if (!endpointMetrics || !endpointMetrics.recent_times) return null;
    
    const recentTimes = endpointMetrics.recent_times.map(time => time * 1000); // Convert to ms
    const labels = Array.from({ length: recentTimes.length }, (_, i) => `Request ${i + 1}`);
    
    return {
      labels,
      datasets: [
        {
          label: 'Response Time (ms)',
          data: recentTimes,
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
          tension: 0.1,
        }
      ]
    };
  };

  // Prepare chart data for system metrics
  const prepareSystemChartData = () => {
    if (!systemMetrics) return null;
    
    return {
      labels: ['Memory', 'CPU'],
      datasets: [
        {
          label: 'Usage (%)',
          data: [systemMetrics.memory.percent, systemMetrics.cpu.percent],
          backgroundColor: [
            'rgba(255, 99, 132, 0.2)',
            'rgba(54, 162, 235, 0.2)',
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
          ],
          borderWidth: 1,
        }
      ]
    };
  };

  // Prepare chart data for file sizes
  const prepareFileSizeChartData = () => {
    if (!systemMetrics || !systemMetrics.storage || !systemMetrics.storage.file_sizes) return null;
    
    const files = Object.keys(systemMetrics.storage.file_sizes).slice(0, 10); // Limit to top 10 files
    const sizes = files.map(file => systemMetrics.storage.file_sizes[file]);
    
    return {
      labels: files,
      datasets: [
        {
          label: 'File Size (bytes)',
          data: sizes,
          backgroundColor: 'rgba(255, 159, 64, 0.2)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1,
        }
      ]
    };
  };

  // If performance monitoring is disabled, redirect to dashboard
  if (!performanceEnabled && !loading) {
    return <Navigate to="/" />;
  }

  if (loading && !metrics) {
    return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
  }

  if (error && !metrics) {
    return <div className="alert alert-danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>;
  }

  const endpointChartData = prepareEndpointChartData();
  const detailedEndpointChartData = prepareDetailedEndpointChartData();
  const systemChartData = prepareSystemChartData();
  const fileSizeChartData = prepareFileSizeChartData();

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5 mb-4">Performance Monitoring</h1>
          
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              {lastUpdated && (
                <span className="text-muted">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </div>
            <div className="d-flex align-items-center">
              <div className="form-check form-switch me-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="autoRefreshSwitch"
                  checked={autoRefresh}
                  onChange={() => setAutoRefresh(!autoRefresh)}
                />
                <label className="form-check-label" htmlFor="autoRefreshSwitch">
                  Auto-refresh
                </label>
              </div>
              <select
                className="form-select form-select-sm me-3"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                disabled={!autoRefresh}
                style={{ width: '100px' }}
              >
                <option value="10">10s</option>
                <option value="30">30s</option>
                <option value="60">1m</option>
                <option value="300">5m</option>
              </select>
              <button
                className="btn btn-primary btn-sm"
                onClick={fetchMetrics}
                disabled={loading}
              >
                {loading ? (
                  <><i className="fas fa-spinner fa-spin me-1"></i> Refreshing...</>
                ) : (
                  <><i className="fas fa-sync-alt me-1"></i> Refresh</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {metrics && systemMetrics && (
        <>
          {/* System Metrics Summary */}
          <div className="row mb-4">
            <div className="col-md-3 mb-3 mb-md-0">
              <div className="card h-100 shadow-sm border-0">
                <div className="card-body text-center p-4">
                  <h3 className="text-muted mb-3">Memory Usage</h3>
                  <div className="display-6 fw-bold text-primary">
                    {systemMetrics.memory.percent.toFixed(1)}%
                  </div>
                  <div className="text-muted mt-2">
                    {formatBytes(systemMetrics.memory.used)} / {formatBytes(systemMetrics.memory.total)}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3 mb-md-0">
              <div className="card h-100 shadow-sm border-0">
                <div className="card-body text-center p-4">
                  <h3 className="text-muted mb-3">CPU Usage</h3>
                  <div className="display-6 fw-bold text-danger">
                    {systemMetrics.cpu.percent.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3 mb-md-0">
              <div className="card h-100 shadow-sm border-0">
                <div className="card-body text-center p-4">
                  <h3 className="text-muted mb-3">Data Size</h3>
                  <div className="display-6 fw-bold text-success">
                    {formatBytes(systemMetrics.storage.data_dir_size)}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card h-100 shadow-sm border-0">
                <div className="card-body text-center p-4">
                  <h3 className="text-muted mb-3">API Endpoints</h3>
                  <div className="display-6 fw-bold text-primary">
                    {metrics.summary.total_endpoints}
                  </div>
                  <div className="text-muted mt-2">
                    {metrics.summary.total_requests.toLocaleString()} requests
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* System Metrics Charts */}
          <div className="row mb-4">
            <div className="col-md-6 mb-4 mb-md-0">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-white py-3">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-server me-2"></i>
                    System Resource Usage
                  </h5>
                </div>
                <div className="card-body">
                  {systemChartData && (
                    <div style={{ height: '300px' }}>
                      <Bar
                        data={systemChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              max: 100,
                              title: {
                                display: true,
                                text: 'Percentage (%)'
                              }
                            }
                          },
                          plugins: {
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `${context.dataset.label}: ${context.raw.toFixed(1)}%`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-white py-3">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-hdd me-2"></i>
                    Top File Sizes
                  </h5>
                </div>
                <div className="card-body">
                  {fileSizeChartData && (
                    <div style={{ height: '300px' }}>
                      <Bar
                        data={fileSizeChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          indexAxis: 'y',
                          scales: {
                            x: {
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Size (bytes)'
                              }
                            }
                          },
                          plugins: {
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `${context.dataset.label}: ${formatBytes(context.raw)}`;
                                }
                              }
                            },
                            legend: {
                              display: false
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* API Metrics */}
          <div className="row mb-4">
            <div className="col">
              <div className="card shadow-sm">
                <div className="card-header bg-white py-3">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-tachometer-alt me-2"></i>
                    API Performance Metrics
                  </h5>
                </div>
                <div className="card-body">
                  {endpointChartData && (
                    <div style={{ height: '400px' }}>
                      <Bar
                        data={endpointChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              type: 'linear',
                              display: true,
                              position: 'left',
                              title: {
                                display: true,
                                text: 'Response Time (ms)'
                              }
                            },
                            y1: {
                              type: 'linear',
                              display: true,
                              position: 'right',
                              grid: {
                                drawOnChartArea: false
                              },
                              title: {
                                display: true,
                                text: 'Request Count'
                              }
                            }
                          },
                          plugins: {
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  if (context.dataset.label === 'Avg Response Time (ms)') {
                                    return `${context.dataset.label}: ${context.raw.toFixed(2)} ms`;
                                  } else {
                                    return `${context.dataset.label}: ${context.raw}`;
                                  }
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Endpoint Details */}
          <div className="row mb-4">
            <div className="col-md-4 mb-4 mb-md-0">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-white py-3">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-list me-2"></i>
                    Endpoints
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {metrics && metrics.endpoints ? (
                      Object.keys(metrics.endpoints).length > 0 ? (
                        Object.keys(metrics.endpoints).map(endpoint => (
                          <button
                            key={endpoint}
                            className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedEndpoint === endpoint ? 'active' : ''}`}
                            onClick={() => handleEndpointSelect(endpoint)}
                          >
                            <div className="text-truncate" style={{ maxWidth: '70%' }}>
                              {endpoint}
                            </div>
                            <div>
                              <span className="badge bg-primary rounded-pill me-2">
                                {metrics.endpoints[endpoint].count}
                              </span>
                              <span className={`badge ${
                                metrics.endpoints[endpoint].avg_response_time < 0.1 ? 'bg-success' : 
                                metrics.endpoints[endpoint].avg_response_time < 0.5 ? 'bg-warning text-dark' : 
                                'bg-danger'
                              }`}>
                                {formatTime(metrics.endpoints[endpoint].avg_response_time)}
                              </span>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-info-circle fa-3x mb-3"></i>
                          <p>No endpoints have been tracked yet. Use the application to generate metrics.</p>
                        </div>
                      )
                    ) : (
                      <div className="text-center py-5">
                        <i className="fas fa-spinner fa-spin fa-3x"></i>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-8">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-white py-3">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-chart-line me-2"></i>
                    {selectedEndpoint ? `Endpoint Details: ${selectedEndpoint}` : 'Select an endpoint to view details'}
                  </h5>
                </div>
                <div className="card-body">
                  {!selectedEndpoint && (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-arrow-left fa-3x mb-3"></i>
                      <p>Select an endpoint from the list to view detailed metrics</p>
                    </div>
                  )}
                  
                  {selectedEndpoint && loading && !endpointMetrics && (
                    <div className="text-center py-5">
                      <i className="fas fa-spinner fa-spin fa-3x mb-3"></i>
                      <p>Loading metrics for {selectedEndpoint}...</p>
                    </div>
                  )}
                  
                  {selectedEndpoint && error && (
                    <div className="alert alert-danger m-3">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}
                  
                  {selectedEndpoint && endpointMetrics && !loading && !error && (
                    <>
                      <div className="row mb-4">
                        <div className="col-md-3 mb-3 mb-md-0">
                          <div className="card bg-light">
                            <div className="card-body text-center">
                              <h6 className="card-title text-muted">Total Requests</h6>
                              <p className="card-text fw-bold fs-4">{endpointMetrics.count}</p>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3 mb-3 mb-md-0">
                          <div className="card bg-light">
                            <div className="card-body text-center">
                              <h6 className="card-title text-muted">Avg Response Time</h6>
                              <p className="card-text fw-bold fs-4">{formatTime(endpointMetrics.avg_response_time)}</p>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3 mb-3 mb-md-0">
                          <div className="card bg-light">
                            <div className="card-body text-center">
                              <h6 className="card-title text-muted">Min Response Time</h6>
                              <p className="card-text fw-bold fs-4">{formatTime(endpointMetrics.min_response_time)}</p>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card bg-light">
                            <div className="card-body text-center">
                              <h6 className="card-title text-muted">Max Response Time</h6>
                              <p className="card-text fw-bold fs-4">{formatTime(endpointMetrics.max_response_time)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="row mb-4">
                        <div className="col">
                          <h6 className="text-muted mb-3">Percentiles</h6>
                          <div className="progress" style={{ height: '25px' }}>
                            <div 
                              className="progress-bar bg-success" 
                              style={{ width: '50%' }}
                              title={`P50: ${formatTime(endpointMetrics.p50_response_time)}`}
                            >
                              P50: {formatTime(endpointMetrics.p50_response_time)}
                            </div>
                            <div 
                              className="progress-bar bg-info" 
                              style={{ width: '25%' }}
                              title={`P75: ${formatTime(endpointMetrics.p75_response_time)}`}
                            >
                              P75
                            </div>
                            <div 
                              className="progress-bar bg-warning" 
                              style={{ width: '15%' }}
                              title={`P90: ${formatTime(endpointMetrics.p90_response_time)}`}
                            >
                              P90
                            </div>
                            <div 
                              className="progress-bar bg-danger" 
                              style={{ width: '10%' }}
                              title={`P99: ${formatTime(endpointMetrics.p99_response_time)}`}
                            >
                              P99
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mt-1">
                            <small className="text-muted">0</small>
                            <small className="text-muted">{formatTime(endpointMetrics.p50_response_time)}</small>
                            <small className="text-muted">{formatTime(endpointMetrics.p75_response_time)}</small>
                            <small className="text-muted">{formatTime(endpointMetrics.p90_response_time)}</small>
                            <small className="text-muted">{formatTime(endpointMetrics.p99_response_time)}</small>
                            <small className="text-muted">{formatTime(endpointMetrics.max_response_time)}</small>
                          </div>
                        </div>
                      </div>
                      
                      {endpointMetrics.recent_times && endpointMetrics.recent_times.length > 0 ? (
                        <div className="row">
                          <div className="col">
                            <h6 className="text-muted mb-3">Recent Response Times</h6>
                            <div style={{ height: '200px' }}>
                              <Line
                                data={detailedEndpointChartData}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  scales: {
                                    y: {
                                      beginAtZero: true,
                                      title: {
                                        display: true,
                                        text: 'Response Time (ms)'
                                      }
                                    }
                                  },
                                  plugins: {
                                    tooltip: {
                                      callbacks: {
                                        label: function(context) {
                                          return `Response Time: ${context.raw.toFixed(2)} ms`;
                                        }
                                      }
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-info">
                              <i className="fas fa-info-circle me-2"></i>
                              No recent response time data available for this endpoint.
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PerformanceMonitoring;
